import { type Size, type ThreeEvent } from '@react-three/fiber';
import React, { useMemo } from 'react';
import { Shape, Vector2 } from 'three';
import { DrawDemarcation } from '@/components/property-selector/draw-components/draw-demarcation';
import { normalizePoint } from '@/components/property-selector/utils';

import { type PatchedPropertyUnit } from '@/types';

type DrawDemarcationProps = {
  properties: PatchedPropertyUnit[];
  size: Size;
  selectedDemarcationId?: string | null;
  onPointerOver?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerOut?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onShapeClick?: (property: PatchedPropertyUnit) => void;
  showLegend?: boolean;
  colorsSettings: string;
  hoverColor: string;
  availableColor: string;
  soldColor: string;
};

export const DrawDemarcationShapes = ({
  properties,
  onShapeClick,
  size,
  onPointerOver,
  onPointerOut,
  selectedDemarcationId,
  showLegend = true,
  colorsSettings,
  hoverColor,
  availableColor,
  soldColor,
}: DrawDemarcationProps) => {
  const demarcations = useMemo(
    () =>
      properties
        .filter(
          (property) => property.demarcationShape != null && property.demarcationShape.length > 0,
        )
        .map((property) => {
          const { demarcationShape: shapePoints, id } = property;
          const shape = new Shape(
            (shapePoints ?? []).map((p) => {
              const normalizedPoint = normalizePoint(p, size);
              return new Vector2(normalizedPoint.x, normalizedPoint.y);
            }),
          );

          return (
            <DrawDemarcation
              key={id}
              property={property}
              shape={shape}
              size={size}
              selected={id === selectedDemarcationId}
              onPointerOver={onPointerOver}
              onPointerOut={onPointerOut}
              onDemarcationClick={onShapeClick}
              showLegend={showLegend}
              colorsSettings={colorsSettings}
              hoverColor={hoverColor}
              availableColor={availableColor}
              soldColor={soldColor}
            />
          );
        }),
    [
      properties,
      size,
      selectedDemarcationId,
      onPointerOver,
      onPointerOut,
      onShapeClick,
      showLegend,
      colorsSettings,
      hoverColor,
      availableColor,
      soldColor,
    ],
  );

  return <>{demarcations}</>;
};
